import { IS_IE } from 'helpers/viewportHelpers'
import { captureWarning } from 'helpers/errorHelpers'
import { CSSProperties } from 'react'

export const getRemSize = () => {
  const { documentElement } = document
  if (!documentElement) {
    throw new Error(`Function getRemSize requires documentElement`)
  }

  const documentComputedStyle = window.getComputedStyle(documentElement)
  if (!documentComputedStyle.fontSize) {
    throw new Error(`Function getRemSize requires document having declared font size`)
  }

  return parseFloat(documentComputedStyle.fontSize)
}

export const remsToPixels = (cssString: string): number => {
  const parsedFloat = parseFloat(cssString)
  if (cssString.includes('rem')) {
    return parsedFloat * getRemSize()
  } else {
    captureWarning('`cssString` does not contain `rem`')
  }

  return parsedFloat
}

/**
 * Returns selector from composed class name
 * @example getClassSelector('first second third') === '.first'
 */
export const getClassSelector = (composed: string) =>
  `.${ composed.split(' ')[0] }`

/**
 * #ffeedd => [255, 238, 221]
 */
export const getRGBFromHex = (hex: string) =>
  [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)]
    .map((x) => parseInt(x, 16)) as [number, number, number]

// TODO @rendalf upgrade to not-only-vertically
export const doElementsVerticallyIntersect = (el1: Element, el2: Element) => {
  const rect1 = el1.getBoundingClientRect()
  const rect2 = el2.getBoundingClientRect()

  return rect1.bottom > rect2.top && rect2.bottom > rect1.top ||
    rect1.bottom < rect2.top && rect2.bottom < rect1.top
}

export function isRangeVisible (top: number, bottom: number, size: number) {
  const isHit = bottom > 0 && top < size
  const hasTopIntersection = top < 0 && bottom > 0
  const hasBottomIntersection = top < size && bottom > size

  return isHit && hasTopIntersection === hasBottomIntersection
}

export const isElementInViewport = (el: Element, getRefinedRect?: (clientRect: ClientRect) => ClientRect) => {
  const rect = el.getBoundingClientRect()
  const refinedRect = getRefinedRect ? getRefinedRect(rect) : rect
  const { top, bottom, left, right } = refinedRect

  const screenHeight = window.innerHeight
  const screenWidth = window.innerWidth

  const vertical = isRangeVisible(top, bottom, screenHeight)
  // a workaround to handle Insight in carousel:
  // because of transition `left` value can be negative
  const horizontal = isRangeVisible(left, right, screenWidth)

  return vertical && horizontal
}

/* ------------------------ DEVICES -------------------------- */
// TODO @pmyagkov this should be definitely replaced with a Modernizr's version
// initial detection
const DOES_SUPPORT_TOUCH = (() => {
  let result = false
  const windowAny = window as any
  // got from here https://patrickhlauke.github.io/touch/touchscreen-detection/
  if (windowAny.PointerEvent && ('maxTouchPoints' in navigator)) {
    // if Pointer Events are supported, just check maxTouchPoints
    if (navigator.maxTouchPoints > 0) {
      result = true
    }
  } else {
    // no Pointer Events...
    if (window.matchMedia && window.matchMedia('(any-pointer:coarse)').matches) {
      // check for any-pointer:coarse which mostly means touchscreen
      result = true
    } else if (windowAny.TouchEvent || ('ontouchstart' in window)) {
      // last resort - check for exposed touch events API / event handler
      result = true
    }
  }

  return result
})()

// use this as minimum as possible
export const doesSupportTouch = () => DOES_SUPPORT_TOUCH

/* ------------------------ DOM ---------------------------- */


export type BlockPositionRelativeToWindow = 'start' | 'end' | 'nearest'
/**
 * This wrapper is written because of weird IE11 behavior when `node.scrollIntoView` is called.
 * https://tangle.atlassian.net/browse/FRO-613
 */
// TODO: remove in favour of `Anchor`
export const smoothScrollInfoView = (node: Element | null, position: BlockPositionRelativeToWindow = 'start'): void => {
  if (!node) {
    return
  }

  if (IS_IE) {
    const nodeRect = node.getBoundingClientRect()
    const nodeEdge = (position === 'start' ? nodeRect.top : nodeRect.bottom)
    window.scrollTo(window.pageXOffset, nodeEdge + window.pageYOffset)

    return
  }

  node.scrollIntoView({ behavior: 'smooth', block: position })
}

export function getVhValue (cssRule: string) {
  const matches = cssRule.match(/([0-9\.]+)vh/)

  if (!matches || matches.length < 2) {
    return null
  }

  return Number(matches[1])
}

export function convertVhToPx (cssRule: string) {
  const percent = getVhValue(cssRule)

  if (percent === null) {
    return ''
  }

  const viewportPx = window.innerHeight
  const pxValue = viewportPx * (percent / 100)
  const vMatch = `${ percent }vh`

  return cssRule.replace(vMatch, `${ pxValue }px`)
}

export const cssVar = <CSSVars>(props: CSSProperties & CSSVars) => {
  return props
}
